import { getCaptureClockMetadata } from '../../utils/captureClock';

export const CARE_SOURCE_KINDS = Object.freeze({
  TEXT: 'text',
  FILE: 'file',
  IMAGE: 'image',
});

export const CARE_SOURCE_CONTEXTS = Object.freeze({
  TIMELINE: 'timeline',
  KNOWLEDGE: 'knowledge',
  PROFILE: 'profile',
  MEDICATION: 'medication',
  GOAL: 'goal',
  DOCUMENT: 'document',
  APPOINTMENT: 'appointment',
  UNKNOWN: 'unknown',
});

export const CARE_SOURCE_DOCUMENT_TYPES = Object.freeze({
  NOTE: 'note',
  SPREADSHEET: 'spreadsheet',
  DOCTOR_NOTE: 'doctor_note',
  SCHOOL_DOCUMENT: 'school_document',
  LEGAL_DOCUMENT: 'legal_document',
  IMAGE: 'image',
  UNKNOWN: 'unknown',
});

export const CARE_SOURCE_TEXT_EXTRACTION_STATUSES = Object.freeze({
  NOT_NEEDED: 'not_needed',
  EXTRACTED: 'extracted',
  PENDING: 'pending',
  FAILED: 'failed',
});

export const CARE_SOURCE_PROCESSING_STATUSES = Object.freeze({
  CAPTURED: 'captured',
  ORGANIZING: 'organizing',
  ORGANIZED: 'organized',
  NEEDS_REVIEW: 'needs_review',
  SAVED_SOURCE: 'saved_source',
  FAILED: 'failed',
});

export const CARE_SOURCE_REVIEW_STATES = Object.freeze({
  NOT_NEEDED: 'not_needed',
  NEEDS_REVIEW: 'needs_review',
  REVIEWED: 'reviewed',
});

export const CARE_SOURCE_USE_CASES = Object.freeze({
  IMPORT_HISTORY: 'import_history',
});

export const CARE_SOURCE_REVIEW_POLICIES = Object.freeze({
  CAREGIVER_APPROVAL_REQUIRED: 'caregiver_approval_required',
});

const getFileExtension = (fileName = '') => {
  const parts = String(fileName || '').trim().toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
};

const toIsoStringOrNull = (value = null) => (
  value instanceof Date && !Number.isNaN(value.getTime()) ? value.toISOString() : null
);

const getDocumentTypeForFile = ({ fileName = '', fileType = '' } = {}) => {
  const extension = getFileExtension(fileName);
  const normalizedType = String(fileType || '').trim().toLowerCase();

  if (['xls', 'xlsx', 'csv', 'tsv'].includes(extension) || normalizedType.includes('spreadsheet')) {
    return CARE_SOURCE_DOCUMENT_TYPES.SPREADSHEET;
  }

  if (['png', 'jpg', 'jpeg', 'heic', 'webp'].includes(extension) || normalizedType.startsWith('image/')) {
    return CARE_SOURCE_DOCUMENT_TYPES.IMAGE;
  }

  if (['doc', 'docx', 'pdf', 'txt', 'rtf'].includes(extension)) {
    return CARE_SOURCE_DOCUMENT_TYPES.NOTE;
  }

  return CARE_SOURCE_DOCUMENT_TYPES.UNKNOWN;
};

export const buildCareSourceLifecycleMetadata = ({
  sourceKind = CARE_SOURCE_KINDS.TEXT,
  sourceContext = CARE_SOURCE_CONTEXTS.TIMELINE,
  documentType = CARE_SOURCE_DOCUMENT_TYPES.NOTE,
  textExtractionStatus = CARE_SOURCE_TEXT_EXTRACTION_STATUSES.EXTRACTED,
  processingStatus = CARE_SOURCE_PROCESSING_STATUSES.CAPTURED,
  reviewState = CARE_SOURCE_REVIEW_STATES.NOT_NEEDED,
} = {}) => ({
  sourceKind,
  sourceContext,
  documentType,
  textExtractionStatus,
  processingStatus,
  reviewState,
});

export const buildCareSourceFileMetadata = ({
  file = null,
  textLength = 0,
  sourceContext = CARE_SOURCE_CONTEXTS.TIMELINE,
  textExtractionStatus = CARE_SOURCE_TEXT_EXTRACTION_STATUSES.EXTRACTED,
  extractionError = null,
  selectedDate = null,
  selectedLocalDate = null,
  sourceUseCase = '',
  reviewPolicy = '',
  importBatchId = '',
  importBatchOrder = null,
} = {}) => {
  const fileName = String(file?.name || '').trim();
  const fileType = String(file?.type || '').trim();
  const documentType = getDocumentTypeForFile({ fileName, fileType });

  return {
    ...buildCareSourceLifecycleMetadata({
      sourceKind: documentType === CARE_SOURCE_DOCUMENT_TYPES.IMAGE
        ? CARE_SOURCE_KINDS.IMAGE
        : CARE_SOURCE_KINDS.FILE,
      sourceContext,
      documentType,
      textExtractionStatus,
    }),
    fileName,
    fileType,
    fileSize: Number(file?.size || 0),
    fileExtension: getFileExtension(fileName),
    textLength: Number(textLength || 0),
    selectedDate: selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
      ? selectedDate.toISOString()
      : (typeof selectedDate === 'string' ? selectedDate : null),
    selectedLocalDate: selectedLocalDate || null,
    ...(String(sourceUseCase || '').trim() ? {
      sourceUseCase: String(sourceUseCase).trim(),
    } : {}),
    ...(String(reviewPolicy || '').trim() ? {
      reviewPolicy: String(reviewPolicy).trim(),
    } : {}),
    ...(String(importBatchId || '').trim() ? {
      importBatchId: String(importBatchId).trim(),
      importBatchOrder: Number(importBatchOrder || 0),
    } : {}),
    extractionError: extractionError ? {
      message: String(extractionError.message || extractionError || '').trim(),
      code: String(extractionError.code || '').trim(),
    } : null,
  };
};

export const buildCareSourceTextMetadata = ({
  sourceName = 'Typed note',
  inputMode = 'composer',
  selectedDate = null,
  selectedLocalDate = null,
  textLength = 0,
  sourceContext = CARE_SOURCE_CONTEXTS.TIMELINE,
} = {}) => ({
  ...buildCareSourceLifecycleMetadata({
    sourceKind: CARE_SOURCE_KINDS.TEXT,
    sourceContext,
    documentType: CARE_SOURCE_DOCUMENT_TYPES.NOTE,
    textExtractionStatus: CARE_SOURCE_TEXT_EXTRACTION_STATUSES.NOT_NEEDED,
  }),
  sourceName,
  inputMode,
  selectedDate: toIsoStringOrNull(selectedDate),
  ...getCaptureClockMetadata(selectedDate),
  selectedLocalDate: selectedLocalDate || null,
  textLength: Number(textLength || 0),
});
